import React, { useEffect, useState, useRef } from 'react';
import { 
  Avatar, 
  Paper, 
  TextField, 
  Button, 
  Stack, 
  Typography, 
  Box, 
  Container,
  Card,
  CardContent,
  useTheme,
  Fade,
  IconButton
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SignUpModal from '../components/SignUpModal';
import Navbar from '../components/Navbar';
import { Link as RouterLink } from 'react-router-dom';

// Counter animation component
const CountUp = ({ end, duration = 2000 }: { end: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef<number>(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!hasStarted) return;
    
    let startTime: number | null = null;
    let animationFrameId: number;

    const updateCount = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      
      const percentage = Math.min(progress / duration, 1);
      const value = Math.floor(percentage * end);
      
      if (countRef.current !== value) {
        countRef.current = value;
        setCount(value);
      }

      if (progress < duration) {
        animationFrameId = requestAnimationFrame(updateCount);
      } else {
        setCount(end);
      }
    };

    animationFrameId = requestAnimationFrame(updateCount);
    
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [end, duration, hasStarted]);

  return (
    <span ref={(el) => {
      if (el && !hasStarted) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setHasStarted(true);
              observer.disconnect();
            }
          },
          { threshold: 0.1 }
        );
        observer.observe(el);
      }
    }}>
      {count}
    </span>
  );
};

// Component for stats with percentage sign
const PercentageCountUp = ({ end }: { end: number }) => {
  return (
    <>
      <CountUp end={end} />%
    </>
  );
};

function Home() {
  const theme = useTheme();
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    // Check if user has seen the modal before
    const hasSeenModal = localStorage.getItem('hasSeenSignUpModal');
    if (!hasSeenModal) {
      // Show modal after a short delay
      const timer = setTimeout(() => {
        setShowSignUpModal(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    // Add event listener for tab close
    const handleBeforeUnload = () => {
      localStorage.removeItem('hasSeenSignUpModal');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Setup intersection observer for stats section
  useEffect(() => {
    if (!statsRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(statsRef.current);
    
    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  const handleCloseModal = () => {
    setShowSignUpModal(false);
    // Mark that user has seen the modal
    localStorage.setItem('hasSeenSignUpModal', 'true');
  };

  const StatCard = ({ value, label, icon }: { value: string; label: string; icon: React.ReactNode }) => {
    const numericValue = parseInt(value.replace(/[^0-9]/g, ''), 10);
    
    return (
      <Paper
        elevation={2} 
        sx={{ 
          p: 2, 
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: 4
          }
        }}
      >
        <Box sx={{ mb: 1 }}>
          {icon}
        </Box>
        <Typography variant="h4" color="primary" fontWeight="bold">
          <CountUp end={numericValue} />
        </Typography>
        <Typography variant="body2">{label}</Typography>
      </Paper>
    );
  };

  return (
    <>
      <Navbar />
      <SignUpModal 
        open={showSignUpModal} 
        onClose={handleCloseModal} 
      />
      
      {/* Hero Section */}
      <Box 
        sx={{
          position: 'relative',
          height: '70vh',
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          mb: 8,
          mt: { xs: 2, sm: 0 },
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: 4,
          bgcolor: theme.palette.primary.main,
          backgroundImage: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)',
        }}
      >
        <Container maxWidth="md">
          <Fade in={true} timeout={1000}>
            <Box textAlign="center">
              <Typography 
                variant="h2" 
                component="h1" 
                fontWeight="bold" 
                color="white"
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' }
                }}
              >
                Richard Otieno for Teachers Union
              </Typography>
              <Typography 
                variant="h5" 
                color="white" 
                sx={{ 
                  mb: 4, 
                  opacity: 0.9,
                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                }}
              >
                Fighting for our teachers, our schools, and our future
              </Typography>
              <Button 
                variant="contained" 
                color="secondary" 
                size="large"
                onClick={() => setShowSignUpModal(true)}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  fontSize: '1.1rem',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Join Our Campaign
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>
      
      {/* Main Content */}
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
          {/* Candidate Info Section */}
          <Box sx={{ width: { xs: '100%', md: '40%' }, p: 2 }}>
            <Card 
              elevation={4} 
              sx={{ 
                height: '100%',
                overflow: 'visible',
                position: 'relative',
                borderRadius: 3
              }}
            >
              <Box sx={{ 
                position: 'absolute', 
                top: -30, 
                left: '50%', 
                transform: 'translateX(-50%)',
                zIndex: 2
              }}>
                <Avatar 
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    bgcolor: theme.palette.primary.main,
                    border: '4px solid white',
                    boxShadow: 2
                  }}
                >
                  <PersonIcon sx={{ fontSize: 48 }} />
                </Avatar>
              </Box>
              <CardContent sx={{ pt: 9, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold">Richard Otieno</Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
                  Candidate for Union President
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, textAlign: 'left' }}>
                  With 15 years of teaching experience, Richard is a passionate advocate for:
                </Typography>
                {['Fair pay for all educators', 'Smaller class sizes', 'Teacher support systems', 'Professional development'].map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircleOutlineIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
          
          {/* Stats Section */}
          <Box 
            ref={statsRef}
            sx={{ width: { xs: '100%', md: '60%' }, p: 2 }}
          >
            <Stack spacing={3}>
              {/* Stats Cards */}
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                  Experience That Matters
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1 }}>
                  <Box sx={{ width: '33.33%', p: 1 }}>
                    <StatCard 
                      value="15" 
                      label="Years Teaching" 
                      icon={<SchoolIcon color="primary" />} 
                    />
                  </Box>
                  <Box sx={{ width: '33.33%', p: 1 }}>
                    <StatCard 
                      value="8" 
                      label="Schools Served" 
                      icon={<LocationOnIcon color="primary" />} 
                    />
                  </Box>
                  <Box sx={{ width: '33.33%', p: 1 }}>
                    <StatCard 
                      value="3" 
                      label="Union Wins" 
                      icon={<CheckCircleOutlineIcon color="primary" />} 
                    />
                  </Box>
                </Box>
              </Box>
              
              {/* District Stats */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                  District-wide Teacher Stats
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', textAlign: 'center' }}>
                  <Box sx={{ width: '33.33%', p: 1 }}>
                    <Typography variant="h5" color="secondary" fontWeight="bold">
                      <CountUp end={500} /> +
                    </Typography>
                    <Typography variant="body2">Teachers in District</Typography>
                  </Box>
                  <Box sx={{ width: '33.33%', p: 1 }}>
                    <Typography variant="h5" color="secondary" fontWeight="bold">
                      <CountUp end={12} />
                    </Typography>
                    <Typography variant="body2">Schools</Typography>
                  </Box>
                  <Box sx={{ width: '33.33%', p: 1 }}>
                    <Typography variant="h5" color="secondary" fontWeight="bold">
                      <PercentageCountUp end={95} />
                    </Typography>
                    <Typography variant="body2">Union Membership</Typography>
                  </Box>
                </Box>
              </Paper>
            </Stack>
          </Box>
        </Box>
        
        {/* News Section */}
        <Box sx={{ mt: 8, mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight="bold">
              Latest News
            </Typography>
            <Button 
              component={RouterLink}
              to="/news"
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(46, 125, 50, 0.04)',
                }
              }}
            >
              View All News
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
            {/* News Card 1 */}
            <Box sx={{ width: { xs: '100%', md: '50%' }, p: 2 }}>
              <Card 
                component={RouterLink} 
                to="/news/union-negotiations"
                sx={{ 
                  height: '100%',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                  }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    background: 'linear-gradient(45deg, #2e7d32 30%, #81c784 90%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  Union Negotiations
                </Box>
                <CardContent>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  >
                    Union Negotiations Progress
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    March 15, 2024
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Significant progress made in negotiations for better working conditions and fair compensation for teachers across the district.
                  </Typography>
                  <Button 
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'rgba(46, 125, 50, 0.04)',
                      }
                    }}
                  >
                    Read More
                  </Button>
                </CardContent>
              </Card>
            </Box>

            {/* News Card 2 */}
            <Box sx={{ width: { xs: '100%', md: '50%' }, p: 2 }}>
              <Card 
                component={RouterLink} 
                to="/news/community-outreach"
                sx={{ 
                  height: '100%',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                  }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    background: 'linear-gradient(45deg, #f9a825 30%, #ffd54f 90%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  Community Outreach
                </Box>
                <CardContent>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  >
                    Community Outreach Success
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    March 10, 2024
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Our campaign's community outreach program has successfully engaged with over 500 local residents, building strong support for education reform.
                  </Typography>
                  <Button 
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'rgba(46, 125, 50, 0.04)',
                      }
                    }}
                  >
                    Read More
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
        
        {/* Sign Up Form */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mt: 6, 
            mb: 4, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
            Sign Up for Updates
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
            <Box sx={{ width: { xs: '100%', md: '66.66%' }, p: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Stay informed about our campaign, upcoming events, and how you can help make a difference.
              </Typography>
              <Stack spacing={2} component="form" autoComplete="off">
                <TextField label="Name" variant="outlined" required fullWidth />
                <TextField label="Phone" variant="outlined" required fullWidth type="tel" />
                <TextField label="Email (optional)" variant="outlined" fullWidth type="email" />
              </Stack>
            </Box>
            <Box sx={{ width: { xs: '100%', md: '33.33%' }, p: 2, display: 'flex', alignItems: 'center' }}>
              <Button 
                variant="contained" 
                color="primary" 
                size="large" 
                type="submit"
                fullWidth
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
                endIcon={<ArrowForwardIcon />}
              >
                Sign Up
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
}

export default Home;

